import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus-контроллер P220-конструктора страниц.
 *
 * Монтируется на `<body>` (см. application layout). Такая установка
 * выбрана потому, что кнопки toolbar-а (⚙/⎘/🗑) и сама панель лежат
 * в разных поддеревьях DOM — панель сиблинг `<main>`. Stimulus
 * `data-action` ищет контроллер вверх по дереву, поэтому общий предок
 * всех этих элементов — `<body>`.
 *
 * Actions, инициируемые со стороны toolbar-а:
 *   data-action="click->p220--constructor#openPanel"
 *   data-action="click->p220--constructor#duplicate"
 *   data-action="click->p220--constructor#delete"
 *
 * Targets (живут внутри панели `p220/shared/_constructor_panel`):
 *   - panel:   корневой `<aside>` панели — на нём меняется
 *              translate-x-full ↔ translate-x-0.
 *   - content: контейнер, куда вставляется HTML формы настроек
 *              (через innerHTML или turbo_stream replace).
 *   - title:   заголовок панели (меняется в зависимости от контента).
 *
 * Values:
 *   - open: Boolean — состояние панели (открыта/закрыта).
 *
 * Если панель не отрисована на странице (пользователь не в режиме
 * конструктора / не в personal workspace), targets отсутствуют,
 * и actions просто тихо не делают ничего — это безопасно.
 */
export default class extends Controller {
  static targets = ["panel", "content", "title", "saveStatus"]
  static values = {
    open: { type: Boolean, default: false }
  }

  connect() {
    // Начальное состояние: если openValue=true — панель сразу видна.
    this._applyOpenState()
    // Дебаунс-таймер для автосохранения формы настроек.
    this._saveTimer = null
  }

  // ── Actions (toolbar buttons) ─────────────────────────────────────────

  /**
   * Клик по кнопке 📋 — копирует идентификатор для чата в буфер обмена.
   * Для data-компонентов — template_code, для single-компонентов — component_id.
   */
  copyTemplateCode(event) {
    const btn = event.currentTarget
    const value = btn.dataset.p220CopyValue
    if (!value) return

    const original = btn.title

    const done = () => {
      btn.classList.remove('bg-indigo-100', 'text-indigo-600', 'hover:bg-indigo-200')
      btn.classList.add('bg-green-100', 'text-green-600')
      btn.title = 'Скопировано!'
      this._showFlashMessage('success', `Скопировано: ${value}`)
      setTimeout(() => {
        btn.classList.remove('bg-green-100', 'text-green-600')
        btn.classList.add('bg-indigo-100', 'text-indigo-600', 'hover:bg-indigo-200')
        btn.title = original
      }, 1200)
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(done)
    } else {
      // Фолбэк для HTTP (не HTTPS) — через временный textarea
      const ta = document.createElement('textarea')
      ta.value = value
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      done()
    }
  }

  /**
   * Клик по кнопке ⚙ в toolbar компонента.
   * Кнопка несёт два data-атрибута:
   *   - data-p220-component-id — id компонента (для fallback);
   *   - data-p220-panel-url — URL серверного endpoint-а, который
   *     вернёт HTML фрагмент с двумя слотами:
   *       <div data-p220-panel-title>...</div>
   *       <div data-p220-panel-body>...</div>
   *
   * Мы делаем обычный fetch (не Turbo Drive — нам не нужна полная
   * навигация), парсим ответ, раскладываем по targets и открываем
   * панель. При ошибке показываем сообщение и всё равно открываем.
   */
  async openPanel(event) {
    event.preventDefault()
    const button = event.currentTarget
    const componentId = button.dataset.p220ComponentId
    const url = button.dataset.p220PanelUrl

    if (!url) {
      this._renderError(`Не задан URL настроек (component #${componentId}).`)
      this.openValue = true
      return
    }

    this._renderLoading()
    this.openValue = true

    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })

      if (!response.ok) {
        this._renderError(`Ошибка загрузки: HTTP ${response.status}`)
        return
      }

      const html = await response.text()
      this._applyPanelFragment(html)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[p220:constructor] fetch failed", err)
      this._renderError(`Не удалось загрузить настройки: ${err.message}`)
    }
  }

  /**
   * Клиентский фильтр списка ItemTemplate в мастере добавления.
   * Вешается на search-input как `input->p220--constructor#filterTemplates`.
   * Прячет элементы с классом `.p220-tpl-item`, чьё `data-tpl-search`
   * не содержит введённой строки (без учёта регистра).
   *
   * Ищем только внутри панели (hasContentTarget) — иначе action
   * не должен был сработать, но на всякий случай проверяем.
   */
  filterTemplates(event) {
    const input = event.currentTarget
    const q = (input.value || "").toLowerCase().trim()
    const root = this.hasContentTarget ? this.contentTarget : document
    const items = root.querySelectorAll(".p220-tpl-item")

    items.forEach(el => {
      const txt = (el.dataset.tplSearch || "").toLowerCase()
      el.style.display = !q || txt.indexOf(q) !== -1 ? "" : "none"
    })
  }

  /**
   * Создание новой страницы через браузерный `prompt`.
   * Используется на кнопке «+» в сайдбаре страниц. Вместо инлайн-поля
   * (которое плохо читается в узком сайдбаре) показываем нативный
   * prompt с предложенным именем «Новая», и на OK собираем скрытую
   * POST-форму с CSRF и сабмитим.
   */
  createPageWithPrompt(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.p220CreateUrl
    if (!url) return

    const raw = window.prompt("Название новой страницы:", "Новая")
    if (raw === null) return // пользователь нажал Cancel
    const name = raw.trim()
    if (!name) return

    const form = document.createElement("form")
    form.method = "POST"
    form.action = url
    form.style.display = "none"

    this._appendHidden(form, "authenticity_token", this._csrfToken())
    this._appendHidden(form, "page[name]", name)

    document.body.appendChild(form)
    form.submit()
  }

  _appendHidden(form, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  /**
   * Клик по карточке ItemTemplate в мастере «+ Добавить компонент».
   * Кнопка несёт три data-атрибута:
   *   data-p220-create-url         — POST endpoint для создания;
   *   data-p220-template-code      — тип компонента (table/filter/record_view);
   *   data-p220-template-item-id   — id выбранного ItemTemplate.
   *
   * Шлём POST, на успех перезагружаем страницу — новый компонент
   * появится на сетке через ComponentGridPlacer при рендере.
   */
  async createComponent(event) {
    event.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.p220CreateUrl
    const templateCode = button.dataset.p220TemplateCode
    const templateItemId = button.dataset.p220TemplateItemId

    // template_item_id требуется только для data-компонентов
    // (table/record_view/filter). Single-блоки шлют его пустым.
    if (!url || !templateCode) return

    // Визуальный feedback — помечаем кнопку как "в работе".
    button.classList.add("opacity-60")
    button.disabled = true

    try {
      const formData = new FormData()
      formData.append("template_code", templateCode)
      if (templateItemId) formData.append("template_item_id", templateItemId)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body: formData,
        credentials: "same-origin"
      })

      if (!response.ok) {
        let msg = `HTTP ${response.status}`
        try { const b = await response.json(); if (b && b.error) msg = b.error } catch { /* noop */ }
        window.alert(`Не удалось создать компонент: ${msg}`)
        button.classList.remove("opacity-60")
        button.disabled = false
        return
      }

      window.location.reload()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[p220:constructor] create failed", err)
      window.alert(`Не удалось создать компонент: ${err.message}`)
      button.classList.remove("opacity-60")
      button.disabled = false
    }
  }

  /**
   * Клик по кнопке ⎘ — дублирование компонента.
   * POST /p220/.../construct/.../components/:id/duplicate. На успех
   * перезагружаем страницу целиком (window.location.reload) — самый
   * простой способ увидеть новый компонент без ручной гидрации
   * контекста. Turbo-append с корректной гидрацией — отдельная задача.
   */
  async duplicate(event) {
    event.preventDefault()
    const button = event.currentTarget
    const url = button.dataset.p220DuplicateUrl

    if (!url) return

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (!response.ok) {
        let msg = `HTTP ${response.status}`
        try { const b = await response.json(); if (b && b.error) msg = b.error } catch { /* noop */ }
        window.alert(`Не удалось скопировать: ${msg}`)
        return
      }

      // Перезагружаем страницу, чтобы показать новую копию на сетке.
      // Режим конструктора (карандаш) сохранится — он живёт на пользователе.
      window.location.reload()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[p220:constructor] duplicate failed", err)
      window.alert(`Не удалось скопировать: ${err.message}`)
    }
  }

  /**
   * Клик по кнопке 🗑 — удаление компонента.
   * DELETE /p220/.../construct/.../components/:id с Accept turbo-stream;
   * ответ (turbo_stream.remove) убирает компонент с сетки.
   * Если панель была открыта для этого же компонента — закрываем её.
   */
  async delete(event) {
    event.preventDefault()
    const button = event.currentTarget
    const componentId = button.dataset.p220ComponentId
    const url = button.dataset.p220DestroyUrl

    if (!url) return
    if (!window.confirm("Удалить компонент?")) return

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this._csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (!response.ok) {
        let msg = `HTTP ${response.status}`
        try { const b = await response.json(); if (b && b.error) msg = b.error } catch { /* noop */ }
        window.alert(`Не удалось удалить: ${msg}`)
        return
      }

      const html = await response.text()
      if (window.Turbo && typeof window.Turbo.renderStreamMessage === "function") {
        window.Turbo.renderStreamMessage(html)
      }

      // Закрываем панель, если она была открыта для только что удалённого
      // компонента. Проверяем по data-атрибуту формы настроек.
      this._closePanelIfFor(componentId)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[p220:constructor] delete failed", err)
      window.alert(`Не удалось удалить: ${err.message}`)
    }
  }

  /**
   * Явное сохранение формы редактора полей шаблона.
   * Собирает данные из таблицы полей и отправляет PATCH.
   */
  async submitForm(event) {
    event.preventDefault()
    const form = event.currentTarget
    const url = form.dataset.p220FormUrl
    if (!url) return

    // Собираем поля из таблицы
    const fields = []
    const rows = form.querySelectorAll('.ud-field-row')
    rows.forEach(row => {
      const nameInput = row.querySelector('input[name="fields[][name]"]')
      const typeSelect = row.querySelector('select[name="fields[][type]"]')
      const requiredCheck = row.querySelector('input[name="fields[][required]"]')
      if (!nameInput || !nameInput.value.trim()) return

      fields.push({
        name: nameInput.value.trim(),
        type: typeSelect ? typeSelect.value : 'text',
        required: requiredCheck ? requiredCheck.checked : false
      })
    })

    this._setSaveStatus("pending", "Сохранение...")

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this._csrfToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ fields: fields }),
        credentials: 'same-origin'
      })

      if (!response.ok) {
        let msg = `HTTP ${response.status}`
        try { const b = await response.json(); if (b && b.error) msg = b.error } catch { /* noop */ }
        this._setSaveStatus("error", `Ошибка: ${msg}`)
        return
      }

      this._setSaveStatus("ok", "Сохранено ✓")
    } catch (err) {
      console.error("[p220:constructor] submitForm failed", err)
      this._setSaveStatus("error", `Ошибка: ${err.message}`)
    }
  }

  /**
   * Показать диалог подтверждения удаления шаблона.
   * Загружает HTML-фрагмент с информацией о зависимостях.
   */
  async showDeleteDialog(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.p220DeleteInfoUrl
    if (!url) return

    try {
      const response = await fetch(url, {
        headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      })

      if (!response.ok) return

      const html = await response.text()
      if (this.hasContentTarget) {
        this.contentTarget.innerHTML = html
      }
      if (this.hasTitleTarget) {
        this.titleTarget.textContent = 'Удаление шаблона'
      }
    } catch (err) {
      console.error("[p220:constructor] showDeleteDialog failed", err)
    }
  }

  /**
   * Активация кнопки «Удалить» при вводе «удалить».
   */
  validateDeleteConfirm(event) {
    const input = event.currentTarget
    const btn = document.getElementById('ud-delete-confirm-btn')
    if (!btn) return

    const valid = input.value.trim().toLowerCase() === 'удалить'
    btn.disabled = !valid
    btn.classList.toggle('opacity-50', !valid)
    btn.classList.toggle('cursor-not-allowed', !valid)
  }

  // ── User Data: field editor actions ─────────────────────────────────

  /**
   * Добавляет пустую строку поля в таблицу редактора шаблона.
   * Кнопка «Добавить поле» использует data-action="click->p220--constructor#add_ud_field".
   */
  add_ud_field(event) {
    event.preventDefault()
    const list = this.element.querySelector('#ud-fields-list')
    if (!list) return

    const FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'bool', 'select', 'image', 'file']
    const typeOptions = FIELD_TYPES.map(t =>
      `<option value="${t}">${t}</option>`
    ).join('')

    const html = `
      <div class="grid grid-cols-[1fr_100px_40px_32px] gap-2 px-2 py-1.5 items-center border-b border-gray-50 hover:bg-gray-50/50 ud-field-row">
        <input type="text" name="fields[][name]" value=""
               class="border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
               placeholder="field_name">
        <select name="fields[][type]"
                class="border border-gray-200 rounded px-1.5 py-1 text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500">
          ${typeOptions}
        </select>
        <div class="text-center">
          <input type="checkbox" name="fields[][required]" value="1"
                 class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
        </div>
        <button type="button" class="ud-remove-field-btn inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
          <i class="fi fi-rr-trash text-[10px]"></i>
        </button>
      </div>
    `
    list.insertAdjacentHTML('beforeend', html)
  }

  /**
   * Удаляет строку поля из таблицы.
   * Делегируется через data-action="click->p220--constructor#remove_ud_field".
   */
  remove_ud_field(event) {
    const btn = event.target.closest('.ud-remove-field-btn')
    if (!btn) return
    const row = btn.closest('.ud-field-row')
    if (row) row.remove()
  }

  /**
   * Подтверждение удаления шаблона.
   */
  async confirmDeleteTemplate(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const url = btn.dataset.p220DeleteUrl
    if (!url || btn.disabled) return

    btn.disabled = true
    btn.textContent = 'Удаление...'

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this._csrfToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        let msg = `HTTP ${response.status}`
        try { const b = await response.json(); if (b && b.error) msg = b.error } catch { /* noop */ }
        window.alert(`Не удалось удалить: ${msg}`)
        btn.disabled = false
        btn.textContent = 'Удалить навсегда'
        return
      }

      // Успех — перезагружаем страницу
      window.location.reload()
    } catch (err) {
      console.error("[p220:constructor] confirmDeleteTemplate failed", err)
      window.alert(`Не удалось удалить: ${err.message}`)
      btn.disabled = false
      btn.textContent = 'Удалить навсегда'
    }
  }

  /**
   * Закрытие панели по крестику / Esc.
   */
  close() {
    this.openValue = false
  }

  /**
   * Автосохранение формы настроек (вызывается на change).
   * Дебаунс 400мс — чтобы несколько быстрых изменений (чекбоксы +
   * width) схлопывались в один запрос.
   */
  submitSettings(event) {
    const form = event.currentTarget.closest("form")
    if (!form) return

    if (this._saveTimer) clearTimeout(this._saveTimer)
    this._setSaveStatus("pending", "Сохранение…")

    this._saveTimer = setTimeout(() => this._submitForm(form), 400)
  }

  async _submitForm(form) {
    const formData = new FormData(form)
    const url = form.getAttribute("action")
    // Rails принимает POST + `_method=patch` — это стандартный
    // Rails-паттерн для подмены HTTP-метода.
    const componentId = form.dataset.p220ComponentId

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "application/json, text/html",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: formData,
        credentials: "same-origin"
      })

      if (!response.ok) {
        let msg = `HTTP ${response.status}`
        try {
          const body = await response.json()
          if (body && body.error) msg = body.error
        } catch { /* noop */ }
        this._setSaveStatus("error", `Ошибка сохранения: ${msg}`)
        return
      }

      // Настройки сохранены. Теперь перерисовываем сам компонент на
      // сетке — дёргаем существующий `reload_component` endpoint,
      // который уже умеет отдавать turbo_stream replace компонента.
      await this._reloadComponentOnGrid(componentId)

      this._setSaveStatus("ok", "Сохранено ✓")
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[p220:constructor] submit failed", err)
      this._setSaveStatus("error", `Не удалось сохранить: ${err.message}`)
    }
  }

  /**
   * Дёргает P220::PagesController#reload_component для указанного
   * id и применяет ответ как turbo_stream на странице.
   *
   * Путь строим от текущего location — мы всегда внутри страницы,
   * для которой правим компонент, значит `location.pathname` — это
   * `/p220/workspace/:wid/pages/:pid`. Добавляем `/reload_component`.
   */
  async _reloadComponentOnGrid(componentId) {
    if (!componentId) return

    // Находим кнопку ⚙ этого компонента — на ней лежит свежий URL
    // panel, из которого можно извлечь workspace_id / page_id.
    // Но проще использовать текущий location + стандартный endpoint.
    const base = window.location.pathname.replace(/\/+$/, "")
    const url = `${base}/reload_component?component_id=${encodeURIComponent(componentId)}`

    const response = await fetch(url, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })

    if (!response.ok) return

    const html = await response.text()
    if (window.Turbo && typeof window.Turbo.renderStreamMessage === "function") {
      window.Turbo.renderStreamMessage(html)
    }
  }

  _setSaveStatus(state, message) {
    if (!this.hasSaveStatusTarget) return
    const el = this.saveStatusTarget
    el.textContent = message
    el.classList.remove("text-gray-400", "text-indigo-600", "text-green-600", "text-red-600")
    switch (state) {
      case "pending": el.classList.add("text-indigo-600"); break
      case "ok":      el.classList.add("text-green-600");  break
      case "error":   el.classList.add("text-red-600");    break
      default:        el.classList.add("text-gray-400")
    }
  }

  // ── State handling ────────────────────────────────────────────────────

  openValueChanged() {
    this._applyOpenState()
  }

  _applyOpenState() {
    // Панель может отсутствовать в DOM (user вне режима конструктора
    // или в чужом workspace) — тогда просто ничего не делаем.
    if (!this.hasPanelTarget) return

    const panel = this.panelTarget
    if (this.openValue) {
      panel.classList.remove("translate-x-full")
      panel.classList.add("translate-x-0")
      panel.setAttribute("aria-hidden", "false")
    } else {
      panel.classList.remove("translate-x-0")
      panel.classList.add("translate-x-full")
      panel.setAttribute("aria-hidden", "true")
    }
  }

  // ── Rendering helpers ────────────────────────────────────────────────

  /**
   * Разбирает HTML-фрагмент ответа сервера и подставляет его части
   * в соответствующие targets. Ожидаемая структура:
   *   <div data-p220-panel-title>...</div>
   *   <div data-p220-panel-body>...</div>
   * Если слотов нет — кладём весь HTML в content, title оставляем.
   */
  _applyPanelFragment(html) {
    const tmp = document.createElement("div")
    tmp.innerHTML = html

    const titleEl = tmp.querySelector("[data-p220-panel-title]")
    const bodyEl  = tmp.querySelector("[data-p220-panel-body]")

    if (titleEl && this.hasTitleTarget) {
      this.titleTarget.textContent = titleEl.textContent.trim()
    }

    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = bodyEl ? bodyEl.innerHTML : html
    }
  }

  _renderLoading() {
    if (this.hasTitleTarget) this.titleTarget.textContent = "Загрузка…"
    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = `
        <div class="flex items-center gap-2 text-sm text-gray-500">
          <div class="w-4 h-4 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin"></div>
          <span>Загрузка настроек…</span>
        </div>
      `
    }
  }

  _renderError(message) {
    if (this.hasTitleTarget) this.titleTarget.textContent = "Ошибка"
    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = `
        <div class="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          ${this._escape(message)}
        </div>
      `
    }
  }

  _escape(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    })[c])
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }

  /**
   * Показывает toast-уведомление в правом нижнем углу (#flash-messages).
   * Аналогичен showFlashMessage в static_grid_drag_controller.
   */
  _showFlashMessage(type, message) {
    const flashContainer = document.getElementById('flash-messages')
    if (!flashContainer) return

    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      warning: 'bg-yellow-500',
      info: 'bg-blue-500'
    }

    const flash = document.createElement('div')
    flash.className = `mb-2 p-3 rounded shadow-lg text-white flex items-center justify-between text-sm ${colors[type] || colors.info}`
    flash.innerHTML = `
      <span>${message}</span>
      <button class="ml-3 hover:text-gray-200 font-bold text-lg leading-none">&times;</button>
    `

    flash.querySelector('button').onclick = () => flash.remove()
    flashContainer.prepend(flash)
    setTimeout(() => flash.remove(), 3000)
  }

  /**
   * Если внутри панели открыта форма настроек того же component_id,
   * что пользователь сейчас удалил/переместил — закрываем панель,
   * чтобы не остаться с формой, которая указывает в никуда.
   */
  _closePanelIfFor(componentId) {
    if (!componentId || !this.hasContentTarget) return
    const form = this.contentTarget.querySelector("form[data-p220-component-id]")
    const activeId = form?.dataset?.p220ComponentId
    if (activeId && activeId.toString() === componentId.toString()) {
      this.openValue = false
    }
  }
}
